/**
 * Client-side base photo upload that posts directly to the backend.
 * Uses one HTTP request per file to `/photos/base/upload` so intermittent connectivity
 * only affects individual files. Retries transient failures with exponential backoff.
 */

import type { BasePhoto } from '@/features/base/types/base-content'
import type { ApiResponse } from '@/shared/types/backend-response.type'
import { PHOTOS } from '@/shared/constants/backend-api'
import {
  DEFAULT_UPLOAD_BASE_DELAY_MS,
  DEFAULT_UPLOAD_MAX_ATTEMPTS,
  executeWithUploadRetries,
  fetchUploadBearerToken,
  defaultUploadShouldRetry,
  isErrorWithStatus,
} from '@/shared/lib/upload-transient-retry'
import type { DirectUploadBatchProgress } from '@/shared/types/direct-upload-progress.type'
import {
  uploadBaseVideoMultipartChunks,
  shouldUseVideoMultipartUpload,
} from '@/features/photos/lib/upload-video-multipart'

const UNSUPPORTED_VIDEO_EXT = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
const UNSUPPORTED_VIDEO_MIME = [
  'video/x-msvideo',
  'video/x-matroska',
  'video/x-flv',
  'video/x-ms-wmv',
  'video/3gpp',
]

type BasePhotoUploadResponse = ApiResponse<BasePhoto[]> & {
  errors?: string[]
  skippedDuplicates?: string[]
}

function parseSuccessBasePhoto(json: unknown): BasePhoto | null {
  if (!json || typeof json !== 'object') return null
  const o = json as Record<string, unknown>
  if (o.success === true && o.data != null && typeof o.data === 'object') {
    return o.data as BasePhoto
  }
  return null
}

function extractErrorMessage(json: unknown): string | undefined {
  if (!json || typeof json !== 'object') return undefined
  const rec = json as Record<string, unknown>
  if (typeof rec.message === 'string') return rec.message
  if (Array.isArray(rec.message) && rec.message.every((x) => typeof x === 'string')) {
    return rec.message.join(' ')
  }
  return undefined
}

function shouldRetryUploadError(error: unknown, attemptIndex: number): boolean {
  if (isErrorWithStatus(error)) {
    const s = error.status ?? 0
    if (s === 400 || s === 403 || s === 404 || s === 413 || s === 422) return false
  }
  return defaultUploadShouldRetry(error, attemptIndex)
}

async function uploadOneBasePhotoDirect(
  regionId: number,
  file: File,
  options?: {
    description?: string
    takenAt?: string
    componentName?: string
  }
): Promise<BasePhoto> {
  return executeWithUploadRetries(
    async () => {
      const token = await fetchUploadBearerToken()
      if (!token) {
        throw Object.assign(new Error('Not authenticated'), { status: 401 })
      }

      const formData = new FormData()
      formData.append('photo', file)
      formData.append('regionId', regionId.toString())
      if (options?.description) formData.append('description', options.description)
      if (options?.takenAt) formData.append('takenAt', options.takenAt)
      if (options?.componentName) formData.append('componentName', options.componentName)

      const res = await fetch(PHOTOS.baseUpload, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      })

      const json = await res.json().catch(() => ({}))

      if (!res.ok) {
        const msg =
          extractErrorMessage(json) ||
          (res.status === 413
            ? 'Request too large. Try fewer or smaller files (e.g. under 50MB per image, 500MB per video).'
            : `HTTP error! status: ${res.status}`)
        throw Object.assign(new Error(msg), { status: res.status })
      }

      const photo = parseSuccessBasePhoto(json)
      if (!photo) {
        const msg = extractErrorMessage(json) || 'Upload succeeded but response was unexpected.'
        throw Object.assign(new Error(msg), { status: res.status })
      }
      return photo
    },
    shouldRetryUploadError,
    { maxAttempts: DEFAULT_UPLOAD_MAX_ATTEMPTS, baseDelayMs: DEFAULT_UPLOAD_BASE_DELAY_MS }
  )
}

export async function uploadBasePhotosDirect(
  regionId: number,
  files: File[],
  options?: {
    description?: string
    takenAt?: string
    componentName?: string
    onProgress?: (progress: DirectUploadBatchProgress) => void
    onFileProgress?: (fileIndex: number, percentWithinFile: number) => void
  }
): Promise<BasePhotoUploadResponse> {
  try {
    if (files.length === 0) {
      return { success: false, message: 'At least one file is required', status: 400 }
    }
    if (files.length > 30) {
      return { success: false, message: 'Maximum 30 files can be uploaded at once', status: 400 }
    }

    for (const file of files) {
      const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ''
      if (UNSUPPORTED_VIDEO_EXT.includes(ext)) {
        return {
          success: false,
          message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`,
          status: 400,
        }
      }
      if (UNSUPPORTED_VIDEO_MIME.includes(file.type.toLowerCase())) {
        return {
          success: false,
          message: `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`,
          status: 400,
        }
      }
    }

    const photos: BasePhoto[] = []
    const errors: string[] = []
    const totalCount = files.length
    const onProgress = options?.onProgress
    const onFileProgress = options?.onFileProgress

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      onProgress?.({
        completedCount: i,
        totalCount,
        activeIndex: i + 1,
        activeFileName: file.name,
      })
      try {
        onFileProgress?.(i, 0)
        const { onProgress: _omitProgress, onFileProgress: _omitFileProgress, ...perFileDto } =
          options ?? {}
        let photo: BasePhoto
        if (shouldUseVideoMultipartUpload(file)) {
          photo = await uploadBaseVideoMultipartChunks(
            regionId,
            file,
            {
              description: perFileDto.description,
              takenAt: perFileDto.takenAt,
              componentName: perFileDto.componentName,
            },
            (chunk) => {
              const pct =
                chunk.fileSize > 0
                  ? Math.min(100, Math.round((chunk.uploadedBytes / chunk.fileSize) * 100))
                  : 0
              onProgress?.({
                completedCount: i,
                totalCount,
                activeIndex: i + 1,
                activeFileName: file.name,
                currentFileBytesUploaded: chunk.uploadedBytes,
                currentFileBytesTotal: chunk.fileSize,
                currentPartNumber: chunk.partNumber,
                currentFilePartsTotal: chunk.totalParts,
              })
              onFileProgress?.(i, pct)
            }
          )
        } else {
          photo = await uploadOneBasePhotoDirect(regionId, file, perFileDto)
        }
        photos.push(photo)
        onFileProgress?.(i, 100)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        errors.push(`${file.name}: ${msg}`)
      }
    }

    onProgress?.({
      completedCount: totalCount,
      totalCount,
      activeIndex: 0,
      activeFileName: null,
    })

    if (photos.length === 0) {
      return {
        success: false,
        message:
          errors.length > 0
            ? `All uploads failed. ${errors.slice(0, 5).join(' ')}${errors.length > 5 ? ' …' : ''}`
            : 'All uploads failed.',
        status: 500,
      }
    }

    return {
      success: true,
      data: photos,
      ...(errors.length > 0 && { errors }),
    }
  } catch (e) {
    console.error('Error uploading base photos (direct):', e)
    return {
      success: false,
      message:
        e instanceof TypeError && e.message === 'Failed to fetch'
          ? 'Upload could not reach the server. Check your connection and try fewer or smaller files.'
          : e instanceof Error
            ? e.message
            : 'Unknown error occurred',
      status: 500,
    }
  }
}
